import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  getClients,
  updateClient,
  getClientOrders,
  getDeletionsStripePending,
} from "./adminApi";
import Profilo from "../pages/Profilo";
import DeleteClientModal from "./DeleteClientModal";
import "./AdminClienti.css";

export default function AdminClientsPage() {
  const token = localStorage.getItem("token");

  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [openId, setOpenId] = useState(null);
  const [orders, setOrders] = useState({ items: [], total: 0, page: 1 });
  const [ordersLoading, setOrdersLoading] = useState(false);

  // cancellazione account
  const [deleteRow, setDeleteRow] = useState(null);
  const [stripePending, setStripePending] = useState([]);

  // sort
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  function toggleSort(col) {
    setPage(1);
    if (col === sortBy) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir(
        col === "created_at" || col === "orders" || col === "last_order_at"
          ? "desc"
          : "asc"
      );
    }
  }

  async function load() {
    try {
      setLoading(true);
      const data = await getClients(token, {
        q,
        page,
        pageSize,
        onlyActive,
        includeDeleted: showDeleted,
        sortBy,
        sortDir,
      });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento clienti");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page, onlyActive, showDeleted, sortBy, sortDir]);

  async function loadStripePending() {
    try {
      const data = await getDeletionsStripePending(token);
      setStripePending(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    loadStripePending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDeleted() {
    setDeleteRow(null);
    await Promise.all([load(), loadStripePending()]);
  }

  async function openDetail(id) {
    try {
      setOpenId(id);
      setOrders({ items: [], total: 0, page: 1 });
      setOrdersLoading(true);
      const o = await getClientOrders(token, id, { page: 1, pageSize: 10 });
      setOrders(o);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento dettaglio");
    } finally {
      setOrdersLoading(false);
    }
  }

  async function toggleActive(row) {
    try {
      await updateClient(token, row.id, { is_active: !row.is_active });
      toast.success("Cliente aggiornato");
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Errore aggiornamento");
    }
  }

  async function loadMoreOrders() {
    if (!openId) return;
    try {
      setOrdersLoading(true);
      const nextPage = (orders.page || 1) + 1;
      const more = await getClientOrders(token, openId, {
        page: nextPage,
        pageSize: 10,
      });
      setOrders((prev) => ({
        page: nextPage,
        total: more.total,
        items: [...(prev.items || []), ...(more.items || [])],
      }));
    } catch (e) {
      console.error(e);
      toast.error("Impossibile caricare altri ordini");
    } finally {
      setOrdersLoading(false);
    }
  }

  const pages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  function statusLabel(s) {
    switch ((s || "").toLowerCase()) {
      case "pending":
      case "creata":
        return "In attesa";
      case "confirmed":
      case "confermata":
        return "Confermata";
      case "picked_up":
      case "ritirata":
        return "Ritirata";
      case "processing":
      case "in_lavorazione":
        return "In lavorazione";
      case "delivered":
      case "consegnata":
        return "Consegnato";
      case "canceled":
      case "annullata":
        return "Annullata";
      default:
        return s || "—";
    }
  }

  function statusClass(s) {
    switch ((s || "").toLowerCase()) {
      case "pending":
      case "creata":
        return "is-pending";
      case "confirmed":
      case "confermata":
        return "is-confirmed";
      case "picked_up":
      case "ritirata":
        return "is-picked";
      case "processing":
      case "in_lavorazione":
        return "is-processing";
      case "delivered":
      case "consegnata":
        return "is-delivered";
      case "canceled":
      case "annullata":
        return "is-canceled";
      default:
        return "is-unknown";
    }
  }

  function orderTagClass(s) {
    switch ((s || "").toLowerCase()) {
      case "delivered":
      case "consegnata":
        return "prf-tag--ok";
      case "canceled":
      case "annullata":
        return "prf-tag--muted"; // o "prf-chip--danger" se vuoi più aggressivo
      case "pending":
      case "creata":
        return "prf-tag--muted";
      case "confirmed":
      case "confermata":
      case "picked_up":
      case "ritirata":
      case "processing":
      case "in_lavorazione":
        return "prf-tag--info";
      default:
        return "prf-tag--muted";
    }
  }

  return (
    <div className="ac-page">
      <header className="ac-header">
        <h1>Clienti</h1>
      </header>

      <main className="ac-main">
        <div className="ac-toolbar">
          <input
            className="ac-input"
            placeholder="Cerca per nome, email o città…"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
          />
          <label className="ac-check">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => {
                setPage(1);
                setOnlyActive(e.target.checked);
              }}
            />
            Solo attivi
          </label>
          <label className="ac-check">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => {
                setPage(1);
                setShowDeleted(e.target.checked);
              }}
            />
            Mostra cancellati
          </label>
        </div>

        {stripePending.length > 0 && (
          <details className="ac-stripe-pending">
            <summary>
              Cancellazioni con Stripe da completare ({stripePending.length})
            </summary>
            <p className="ac-del-muted">
              Account già cancellati nel DB per cui Stripe non ha confermato la chiusura di
              abbonamenti/customer: completare dalla dashboard Stripe.
            </p>
            <ul className="ac-stripe-list">
              {stripePending.map((a) => (
                <li key={a.id}>
                  <div>
                    <strong>Cliente #{a.target_id}</strong>{" "}
                    <span className="ac-del-muted">
                      {new Date(a.created_at).toLocaleString("it-IT")} ·{" "}
                      {a.source === "deletion_request" ? "richiesta utente" : "admin"} · audit #{a.id}
                    </span>
                  </div>
                  <span className={`ac-stripe-status is-${a.external_status}`}>
                    {a.external_status}
                  </span>
                  {Array.isArray(a.stripe_items) && a.stripe_items.length > 0 && (
                    <ul className="ac-stripe-items">
                      {a.stripe_items.map((it, i) => (
                        <li key={i}>
                          {it.kind} {it.ref}: {it.result}
                          {it.error_code ? ` (${it.error_code})` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        <section className="ac-table">
          {/* header */}
          <div className="ac-row ac-row--head">
            <div className="ac-cell">
              <button
                className={`ac-thbtn ${
                  sortBy === "name"
                    ? "is-active " + (sortDir === "desc" ? "is-desc" : "is-asc")
                    : ""
                }`}
                type="button"
                onClick={() => toggleSort("name")}
              >
                Nome <span className="ac-caret">▾</span>
              </button>
            </div>

            <div className="ac-cell">
              <button
                className={`ac-thbtn ${
                  sortBy === "email"
                    ? "is-active " + (sortDir === "desc" ? "is-desc" : "is-asc")
                    : ""
                }`}
                type="button"
                onClick={() => toggleSort("email")}
              >
                Email <span className="ac-caret">▾</span>
              </button>
            </div>

            <div className="ac-cell">
              <button
                className={`ac-thbtn ${
                  sortBy === "phone"
                    ? "is-active " + (sortDir === "desc" ? "is-desc" : "is-asc")
                    : ""
                }`}
                type="button"
                onClick={() => toggleSort("phone")}
              >
                Telefono <span className="ac-caret">▾</span>
              </button>
            </div>

            <div className="ac-cell">
              <button
                className={`ac-thbtn ${
                  sortBy === "citta"
                    ? "is-active " + (sortDir === "desc" ? "is-desc" : "is-asc")
                    : ""
                }`}
                type="button"
                onClick={() => toggleSort("citta")}
              >
                Città <span className="ac-caret">▾</span>
              </button>
            </div>

            <div className="ac-cell">
              <button
                className={`ac-thbtn ${
                  sortBy === "provincia"
                    ? "is-active " + (sortDir === "desc" ? "is-desc" : "is-asc")
                    : ""
                }`}
                type="button"
                onClick={() => toggleSort("provincia")}
              >
                Prov <span className="ac-caret">▾</span>
              </button>
            </div>

            <div className="ac-cell ac-center">
              <button
                className={`ac-thbtn ${
                  sortBy === "orders"
                    ? "is-active " + (sortDir === "desc" ? "is-desc" : "is-asc")
                    : ""
                }`}
                type="button"
                onClick={() => toggleSort("orders")}
                title="Numero ordini"
                style={{ justifyContent: "center" }}
              >
                Ordini <span className="ac-caret">▾</span>
              </button>
            </div>

            <div className="ac-cell ac-center">Azioni</div>
          </div>

          {/* body */}
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div className="ac-row" key={`s${i}`}>
                <div className="ac-cell ac-skel">—</div>
                <div className="ac-cell ac-skel">—</div>
                <div className="ac-cell ac-skel">—</div>
                <div className="ac-cell ac-skel">—</div>
                <div className="ac-cell ac-skel">—</div>
                <div className="ac-cell ac-skel">—</div>
                <div className="ac-cell ac-skel">—</div>
              </div>
            ))
          ) : rows.length === 0 ? (
            <div className="ac-empty">Nessun cliente</div>
          ) : (
            rows.map((r) => (
              <div className="ac-row" key={r.id}>
                <div className="ac-cell ac-cell--name" data-label="Nome">
                  <div className="ac-strong">
                    {r.name} - {r.id}
                    {r.deleted_at && <span className="ac-deleted-badge">Cancellato</span>}
                  </div>
                </div>

                <div className="ac-cell ac-cell--email" data-label="Email">
                  {r.email || "—"}
                </div>

                <div className="ac-cell ac-cell--tel" data-label="Telefono">
                  {r.phone || "—"}
                </div>

                <div className="ac-cell ac-cell--city" data-label="Città">
                  {r.citta || "—"}
                </div>

                <div
                  className="ac-cell ac-cell--provincia"
                  data-label="Provincia"
                >
                  {r.provincia || "—"}
                </div>

                <div className="ac-cell ac-cell--orders" data-label="Ordini">
                  <span className="ac-badge">
                    <span>{r.orders_count || 0}</span>
                  </span>
                  {/*
                  <span
                    className="ac-lastorder"
                    title={
                      r.last_order_at
                        ? new Date(r.last_order_at).toLocaleString("it-IT", {
                            dateStyle: "full",
                            timeStyle: "short",
                          })
                        : ""
                    }
                  >
                    {r.last_order_at
                      ? new Date(r.last_order_at).toLocaleDateString("it-IT")
                      : "—"}
                  </span>*/}
                </div>

                <div className="ac-cell ac-cell--actions" data-label="Azioni">
                  <div className="ac-actions">
                    <button
                      className="ac-btn ac-iconbtn"
                      title="Dettagli"
                      onClick={() => openDetail(r.id)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                    {!r.deleted_at && (
                    <button
                      className={`ac-btn ac-iconbtn ${r.is_active ? "ac-iconbtn--danger" : "ac-iconbtn--success"}`}
                      title={r.is_active ? "Blocca" : "Attiva"}
                      onClick={() => toggleActive(r)}
                    >
                      {r.is_active
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                      }
                    </button>
                    )}
                    {!r.deleted_at && (
                      <button
                        className="ac-btn ac-iconbtn ac-iconbtn--danger"
                        title="Elimina cliente"
                        aria-label="Elimina cliente"
                        onClick={() => setDeleteRow(r)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      </button>
                    )}
                    <button
                      className="ac-btn ac-iconbtn"
                      title="Copia email"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(r.email || "");
                          toast.success("Email copiata");
                        } catch {
                          toast.error("Impossibile copiare");
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </section>

        {/* paginazione */}
        {pages > 1 && (
          <div className="ac-pagination">
            <button
              className="ac-pagebtn"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              «
            </button>
            <span>
              Pagina {page} di {pages}
            </span>
            <button
              className="ac-pagebtn"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              »
            </button>
          </div>
        )}
      </main>

      {deleteRow && (
        <DeleteClientModal
          token={token}
          client={deleteRow}
          onClose={() => setDeleteRow(null)}
          onDeleted={handleDeleted}
        />
      )}

      {/* MODALE DETTAGLIO */}
      {openId && (
        <div className="ac-overlay" role="dialog" aria-modal="true">
          <div className="ac-modal">
            <div className="ac-modal-h">
              <div className="ac-title">Cliente #{openId}</div>
              <button className="ac-close" onClick={() => setOpenId(null)}>
                ✕
              </button>
            </div>

            <div className="ac-modal-body">
              <div className="ac-form">
                <div className="ac-profile-embed">
                  <Profilo embedded targetUserId={openId} />
                </div>                
              </div>
            </div>

            <div className="ac-modal-f">
              <button className="ac-btn" onClick={() => setOpenId(null)}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
